import { fetchSenderConsoleEvents, fetchSenderConsoleSummary, type SenderConsoleSummary } from '@/api/sender-console';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { $features } from '@/entities/features-entity';
import SenderLoadError from '@/pages/sender/components/sender-load-error';
import SenderMetricGrid from '@/pages/sender/components/sender-metric-grid';
import { senderSurfaceClass } from '@/pages/sender/components/sender-surface';
import { nowQueueCount } from '@/pages/sender/now-state';
import { SenderScreen, SENDER_ADMIN_DISABLED } from '@/pages/sender/sender-screen';
import { useUnit } from 'effector-react';
import { useCallback, useEffect, useState } from 'react';

const POLL_MS = 3000;
const SEARCH_DEBOUNCE_MS = 300;

export default function SenderSpammerConsolePage() {
	const { senderConsoleEnabled } = useUnit($features);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [summary, setSummary] = useState<SenderConsoleSummary | null>(null);
	const [events, setEvents] = useState<Awaited<ReturnType<typeof fetchSenderConsoleEvents>>['items']>([]);
	const [searchText, setSearchText] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');

	useEffect(() => {
		const timer = window.setTimeout(() => setDebouncedSearch(searchText.trim()), SEARCH_DEBOUNCE_MS);
		return () => window.clearTimeout(timer);
	}, [searchText]);

	const refresh = useCallback(async () => {
		if (!senderConsoleEnabled) {
			setLoading(false);
			return;
		}
		try {
			const [nextSummary, nextEvents] = await Promise.all([
				fetchSenderConsoleSummary(),
				fetchSenderConsoleEvents({
					limit: 30,
					...(debouncedSearch ? { q: debouncedSearch } : {}),
				}),
			]);
			setSummary(nextSummary);
			setEvents(nextEvents.items);
			setError(false);
		} catch {
			setError(true);
		} finally {
			setLoading(false);
		}
	}, [senderConsoleEnabled, debouncedSearch]);

	useEffect(() => {
		void refresh();
		if (!senderConsoleEnabled) return;
		const timer = window.setInterval(() => void refresh(), POLL_MS);
		return () => window.clearInterval(timer);
	}, [refresh, senderConsoleEnabled]);

	if (!senderConsoleEnabled) {
		return (
			<SenderScreen title='Ход рассылки'>
				<p className='text-[15px]'>{SENDER_ADMIN_DISABLED}</p>
			</SenderScreen>
		);
	}

	const queue = nowQueueCount(summary);

	if (error && !summary) {
		return (
			<SenderScreen title='Ход рассылки'>
				<SenderLoadError
					onRetry={() => {
						setLoading(true);
						setError(false);
						void refresh();
					}}
				/>
			</SenderScreen>
		);
	}

	return (
		<SenderScreen title='Ход рассылки'>
			{loading && !summary ? (
				<div className='flex justify-center py-8'>
					<Spinner />
				</div>
			) : (
				<SenderMetricGrid
					columns={4}
					metrics={[
						{ label: 'Обработано', value: summary ? String(summary.accountsProcessed) : '—' },
						{ label: 'Успех', value: summary ? String(summary.messagesSuccess) : '—', tone: 'ok' },
						{ label: 'Ошибки', value: summary ? String(summary.messagesErrors) : '—', tone: 'bad' },
						{ label: 'Осталось', value: queue == null ? '—' : String(queue), tone: 'muted' },
					]}
				/>
			)}
			{summary && summary.byPreset.length > 0 ? (
				<ul className='flex flex-col gap-2'>
					{summary.byPreset.map(row => (
						<li key={`${row.presetId ?? 'none'}-${row.name}`} className={senderSurfaceClass + ' px-3 py-2'}>
							<span className='block text-[15px] font-medium'>{row.name}</span>
							<span className='block text-[13px] text-muted-foreground'>
								Успех {row.messages} · Ошибки {row.errors}
							</span>
						</li>
					))}
				</ul>
			) : null}
			<Input
				value={searchText}
				onChange={event => setSearchText(event.target.value)}
				placeholder='Поиск'
				aria-label='Поиск'
				className='min-h-11 text-[15px]'
			/>
			<Button type='button' variant='outline' className='min-h-11 w-fit' onClick={() => void refresh()}>
				Обновить
			</Button>
			{error ? (
				<SenderLoadError
					onRetry={() => {
						setLoading(true);
						setError(false);
						void refresh();
					}}
				/>
			) : null}
			{!error && events.length === 0 ? (
				<p className='text-[15px] text-muted-foreground'>
					{debouncedSearch ? 'Ничего не найдено' : 'Событий пока нет'}
				</p>
			) : events.length > 0 ? (
				<ul className='flex flex-col gap-2'>
					{events.map(event => (
						<li
							key={event.id}
							className={
								senderSurfaceClass +
								' px-3 py-2 ' +
								(event.level === 'error' ? 'text-destructive' : 'text-[15px]')
							}
						>
							<span className='text-[13px] text-muted-foreground'>{new Date(event.at).toLocaleString('ru-RU')} </span>
							{event.presetName ? <span className='font-medium'>{event.presetName} </span> : null}
							{event.message}
						</li>
					))}
				</ul>
			) : null}
		</SenderScreen>
	);
}
